<template>
    <b-modal
        v-model="visible"
        title="Заявка на аннулирование отчётов"
        size="lg"
        :ok-disabled="!canSubmit || busy"
        :cancel-disabled="busy"
        ok-title="Создать заявку"
        cancel-title="Отмена"
        @ok="submit"
    >
        <b-alert v-if="error" show variant="danger" class="mb-3">
            {{ error }}
        </b-alert>

        <b-alert show variant="danger" class="mb-3">
            Аннулированные отчёты сохраняются в системе, но перестают учитываться во всех расчётах.
            Отменить аннулирование через обычный интерфейс нельзя.
        </b-alert>

        <div v-if="normalizedReports.length" class="report-delete-modal">
            <div class="report-delete-modal__summary">
                <div>
                    <small>Объект</small>
                    <strong>{{ objectName }}</strong>
                </div>
                <div>
                    <small>Отчётов</small>
                    <strong>{{ normalizedReports.length }}</strong>
                </div>
                <div>
                    <small>Куратор</small>
                    <strong>{{ curatorName }}</strong>
                </div>
            </div>

            <b-alert v-if="!sameObject" show variant="warning" class="mb-3">
                В одной заявке можно аннулировать отчёты только одного объекта.
            </b-alert>

            <div class="report-delete-modal__common">
                <b-form-input
                    v-model="commonCause"
                    size="sm"
                    :maxlength="causeLimit"
                    placeholder="Общая причина аннулирования"
                />
                <b-button
                    size="sm"
                    variant="outline-secondary"
                    :disabled="!commonCause.trim()"
                    @click="applyCommonCause"
                >
                    Применить ко всем
                </b-button>
            </div>

            <div class="report-delete-modal__items">
                <div
                    v-for="item in normalizedReports"
                    :key="item.shift_id"
                    class="report-delete-modal__item"
                >
                    <div class="report-delete-modal__item-head">
                        <strong>№{{ item.shift_id }}</strong>
                        <span>{{ formatDate(item.date_time_begin) }}</span>
                        <a
                            v-if="item.telegram_url"
                            :href="item.telegram_url"
                            target="_blank"
                            rel="noopener"
                            @click.stop
                        >
                            Telegram
                        </a>
                    </div>
                    <AppTextarea
                        :value="causeByShift[item.shift_id] || ''"
                        size="sm"
                        :maxlength="causeLimit"
                        placeholder="Причина аннулирования"
                        @input="value => setCause(item.shift_id, value)"
                    />
                    <small
                        class="d-block mt-1"
                        :class="causeByShift[item.shift_id] ? 'text-muted' : 'text-danger'"
                    >
                        {{ causeByShift[item.shift_id] ? `Использовано ${causeByShift[item.shift_id].length} из ${causeLimit}` : 'Причина обязательна' }}
                    </small>
                </div>
            </div>

            <b-form-group class="mt-3 mb-0">
                <b-form-radio v-model="sendOnCreate" name="report-delete-send" :value="false">
                    Сохранить как черновик
                </b-form-radio>
                <b-form-radio v-model="sendOnCreate" name="report-delete-send" :value="true">
                    {{ sendText }}
                </b-form-radio>
            </b-form-group>
        </div>
        <div v-else class="text-muted text-center py-3">
            Отчёты для аннулирования не выбраны
        </div>

        <template #modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="outline-secondary" :disabled="busy" @click="cancel()">
                Отмена
            </b-button>
            <b-button size="sm" variant="danger" :disabled="!canSubmit || busy" @click="ok()">
                <b-spinner v-if="busy" small class="mr-1" />
                Создать заявку
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

function fullName(user) {
    if (!user) return '';
    return `${user.last_name || ''} ${user.first_name || ''}`.trim();
}

export default {
    name: 'ReportDeleteRequestModal',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        reports: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            busy: false,
            error: '',
            commonCause: '',
            causeByShift: {},
            sendOnCreate: false,
            causeLimit: 100
        };
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        currentUser() {
            return this.$store.getters.currentUser || {};
        },
        normalizedReports() {
            return (this.reports || [])
                .filter(report => report && report.shift_id && !report.is_annulled)
                .map(report => {
                    const object = report.object || {};
                    const curator = object.curator || {};
                    return {
                        shift_id: Number(report.shift_id),
                        building_process_id: Number(report.building_process_id || object.process_id || 0),
                        object_name: object.name || report.object_name || '',
                        curator_id: Number(report.curator_id || object.curator_id || curator.user_id || 0),
                        curator_name: fullName(curator),
                        date_time_begin: report.date_time_begin,
                        telegram_message_id: report.telegram_message_id || null,
                        telegram_url: report.telegram_url || ''
                    };
                });
        },
        firstReport() {
            return this.normalizedReports[0] || null;
        },
        sameObject() {
            if (!this.firstReport) return true;
            const processId = this.firstReport.building_process_id;
            return this.normalizedReports.every(item => item.building_process_id === processId);
        },
        objectName() {
            return this.firstReport && this.firstReport.object_name ? this.firstReport.object_name : '-';
        },
        curatorName() {
            return this.firstReport && this.firstReport.curator_name ? this.firstReport.curator_name : '-';
        },
        sendText() {
            return this.firstReport && Number(this.firstReport.curator_id) === Number(this.currentUser.userId)
                ? 'Согласовать и аннулировать'
                : 'Отправить куратору на согласование';
        },
        canSubmit() {
            return Boolean(
                this.normalizedReports.length &&
                this.sameObject &&
                this.firstReport &&
                this.firstReport.building_process_id &&
                this.firstReport.curator_id &&
                this.normalizedReports.every(item => String(this.causeByShift[item.shift_id] || '').trim())
            );
        }
    },
    watch: {
        async value(value) {
            if (value) {
                this.resetForm();
                this.busy = true;
                try {
                    const {data} = await api.get('/api/requests/items/report-annulment/config');
                    if (!this.visible) return;
                    if (data.enabled) {
                        if (!data.can_create) { this.error = 'Создание аннулирования недоступно по настройкам типа заявки.'; return; }
                        const query = {requestTypeId: data.request_type_id,
                            buildingProcessId: this.firstReport && this.firstReport.building_process_id,
                            reports: this.normalizedReports.map(row => row.shift_id).join(',')};
                        if (this.$route.query.company) query.company = this.$route.query.company;
                        await this.$router.push({name: 'create-request', query});
                        this.visible = false;
                    }
                } catch (e) { this.error = 'Не удалось открыть заявку на аннулирование. Повторите попытку.'; }
                finally { this.busy = false; }
            }
        },
        reports() {
            if (this.visible) {
                this.resetForm();
            }
        }
    },
    methods: {
        resetForm() {
            const nextCauses = {};
            this.normalizedReports.forEach(item => {
                nextCauses[item.shift_id] = '';
            });
            this.causeByShift = nextCauses;
            this.commonCause = '';
            this.error = '';
            this.sendOnCreate = false;
        },
        formatDate(value) {
            return value ? this.$moment(value).format('DD.MM.YYYY dd HH:mm') : '-';
        },
        normalizeCause(value) {
            return String(value || '').slice(0, this.causeLimit);
        },
        setCause(shiftId, value) {
            this.$set(this.causeByShift, shiftId, this.normalizeCause(value));
        },
        applyCommonCause() {
            const value = this.normalizeCause(this.commonCause.trim());
            this.normalizedReports.forEach(item => {
                this.$set(this.causeByShift, item.shift_id, value);
            });
        },
        async submit(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.canSubmit || this.busy) {
                return;
            }
            this.error = '';
            if (this.sendOnCreate && Number(this.firstReport.curator_id) === Number(this.currentUser.userId)) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Вы куратор объекта. После согласования отчёты будут сразу аннулированы и исключены из всех расчётов. Отменить это через обычный интерфейс нельзя. Продолжить?',
                    {
                        okTitle: 'Аннулировать',
                        cancelTitle: 'Отмена',
                        okVariant: 'danger',
                        centered: true
                    }
                );
                if (!confirmed) {
                    return;
                }
            }

            this.busy = true;
            try {
                const payload = {
                    inspector: this.firstReport.curator_id,
                    buildingProcess: this.firstReport.building_process_id,
                    shifts: this.normalizedReports.map(item => ({
                        shift_id: item.shift_id,
                        cause: String(this.causeByShift[item.shift_id] || '').trim(),
                        telegram_message_id: item.telegram_message_id,
                        date_time_begin: item.date_time_begin
                    }))
                };
                const request = await this.$store.dispatch('deleteShiftReport_createRequest', payload);
                let approved = false;
                let sent = false;
                if (this.sendOnCreate && request) {
                    const result = await this.$store.dispatch('deleteShiftReport_submitCreatedRequest', request);
                    approved = result.approved;
                    sent = result.sent;
                }
                notifyInstallPromotionSuccess({source: 'report-delete-request-created'});
                this.visible = false;
                this.$emit('created', {request, approved, sent});
            } catch (err) {
                this.error = err && err.response && err.response.data && err.response.data.msg
                    ? err.response.data.msg
                    : 'Не удалось создать заявку на аннулирование';
            } finally {
                this.busy = false;
            }
        }
    }
}
</script>

<style scoped>
.report-delete-modal {
    display: grid;
    gap: 12px;
}

.report-delete-modal__summary {
    display: grid;
    gap: 8px;
    grid-template-columns: minmax(0, 1fr) 90px minmax(160px, .7fr);
}

.report-delete-modal__summary > div {
    background: #f8f9fa;
    border: 1px solid #e5e9ef;
    border-radius: 6px;
    padding: 8px;
}

.report-delete-modal__summary small {
    color: #6c757d;
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.report-delete-modal__common {
    display: grid;
    gap: 8px;
    grid-template-columns: minmax(0, 1fr) auto;
}

.report-delete-modal__items {
    display: grid;
    gap: 8px;
    max-height: 42vh;
    overflow: auto;
    padding-right: 2px;
}

.report-delete-modal__item {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px;
}

.report-delete-modal__item-head {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
}

.report-delete-modal__item-head span {
    color: #6c757d;
    font-size: 12px;
}

@media (max-width: 767.98px) {
    .report-delete-modal__summary,
    .report-delete-modal__common {
        grid-template-columns: 1fr;
    }

    .report-delete-modal__items {
        max-height: 50vh;
    }
}
</style>
